import React, {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { BrowserRouter, Link, Route, Routes, useLocation } from 'react-router';

import Icon from './Components/Icon';
import githubLogo from './github-logo.svg';
import linkedinLogo from './linkedin-logo.svg';
import './App.css';

const FloatingAssistant = lazy(
  () => import('./Components/FloatingAssistant/FloatingAssistant'),
);

const contactLinks = {
  email: 'mailto:sfbowen4.5@gmail.com',
  github: 'https://github.com/sfbowen4',
  linkedin: 'https://www.linkedin.com/in/sfbowen4/',
  website: 'https://www.sfbowen4.com/',
};

const profiles = [
  { label: 'Email', href: contactLinks.email, handle: 'sfbowen4.5@gmail.com' },
  { label: 'GitHub', href: contactLinks.github, handle: 'github.com/sfbowen4' },
  {
    label: 'LinkedIn',
    href: contactLinks.linkedin,
    handle: 'linkedin.com/in/sfbowen4',
  },
];

const navLinks = [
  { label: 'About', to: '/about' },
  { label: 'Projects', to: '/projects' },
  { label: 'Contact', to: '/contact' },
];

const certifications = [
  {
    label: 'Microsoft Certified: Power Platform Developer Associate (PL-400)',
    href: 'https://learn.microsoft.com/api/credentials/share/en-us/sfbowen4/4062A8B576053BEE?sharingId=4B2B09F6A2327819',
  },
  {
    label: 'Microsoft Certified: Azure Developer Associate (AZ-204)',
    href: 'https://learn.microsoft.com/api/credentials/share/en-us/sfbowen4/4358E77980EC8924?sharingId=4B2B09F6A2327819',
  },
  {
    label: 'Microsoft Certified: Azure AI Engineer Associate (AI-102)',
    href: 'https://learn.microsoft.com/api/credentials/share/en-us/sfbowen4/D04D5DEC787972DF?sharingId=4B2B09F6A2327819',
  },
];

const languages = [
  'C#',
  'JavaScript',
  'TypeScript',
  'Python',
  'C',
  'C++',
  'Swift',
];

const technologies = [
  'Agentic AI harnesses',
  'Microsoft Power Platform',
  '.NET',
  'Angular',
  'Azure',
  'CI/CD',
  'Docker',
  'Entity Framework',
  'Git',
  'React',
  'React Native',
  'Microsoft SQL',
  'MySQL',
  'PostgreSQL',
];

const projects = [
  {
    id: 'code-from-the-couch',
    name: 'Code From The Couch',
    kind: 'WEB PLATFORM',
    dates: '2025—Present',
    href: 'https://www.codefromthecouch.com/',
    description:
      'A creator publishing and discovery platform for browser games and apps.',
    details: [
      'Project pages bring together playable builds and creator profiles.',
      'Listings include supported devices and release details.',
    ],
    linkLabel: 'Visit Code From The Couch',
  },
  {
    id: 'doorguy',
    name: 'DoorGuy',
    kind: 'iOS APP',
    dates: '2023—2025',
    href: 'https://apps.apple.com/us/app/doorguy/id6446214998',
    description:
      'An iOS nightlife app for finding crowdsourced cover prices, drink deals, and happy hours.',
    details: [
      'Recent and anonymous check-ins help people see current venue information.',
      'The App Store lists James Dolan as the developer and © Jay Dolan & Stephen Bowen.',
    ],
    linkLabel: 'View DoorGuy on the App Store',
  },
];

function TopNav() {
  const { pathname } = useLocation();

  return (
    <header className="site-header">
      <nav className="top-nav" aria-label="Primary navigation">
        <Link className="brand-name" to="/" aria-label="Stephen Bowen">
          <span className="brand-full">Stephen Bowen</span>
          <span className="brand-short" aria-hidden="true">
            SB
          </span>
        </Link>

        <div className="nav-end">
          <div className="nav-links">
            {navLinks.map(({ label, to }) => {
              const isActive =
                pathname === to || (to === '/about' && pathname === '/');

              return (
                <Link
                  key={to}
                  aria-current={isActive ? 'page' : undefined}
                  className={`nav-link${isActive ? ' nav-link-active' : ''}`}
                  to={to}
                >
                  {label}
                </Link>
              );
            })}
          </div>
          <div
            className="nav-socials"
            role="group"
            aria-label="Stephen Bowen profiles"
          >
            <a
              className="nav-social-link"
              href={contactLinks.github}
              target="_blank"
              rel="noreferrer"
              aria-label="Stephen Bowen on GitHub"
            >
              <img src={githubLogo} alt="" />
            </a>
            <a
              className="nav-social-link"
              href={contactLinks.linkedin}
              target="_blank"
              rel="noreferrer"
              aria-label="Stephen Bowen on LinkedIn"
            >
              <img src={linkedinLogo} alt="" />
            </a>
          </div>
        </div>
      </nav>
    </header>
  );
}

function RouteEffects() {
  const { hash, pathname, key, state } = useLocation();
  const previousKey = useRef(key);
  useLayoutEffect(() => {
    document.title =
      pathname === '/projects'
        ? 'Projects — Stephen Bowen'
        : pathname === '/contact'
          ? 'Contact — Stephen Bowen'
          : 'Stephen Bowen — Software Engineer';
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute(
        'href',
        `https://www.sfbowen4.com${pathname === '/about' ? '/' : pathname}`,
      );
    const target = hash
      ? document.getElementById(hash.slice(1))
      : document.querySelector<HTMLElement>('#main-content h1');
    if (hash && target)
      target.scrollIntoView({ block: 'start', behavior: 'auto' });
    else window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    const fromAssistant = (state as { fromAssistant?: boolean } | null)
      ?.fromAssistant;
    if (previousKey.current !== key && !fromAssistant)
      target?.focus({ preventScroll: true });
    previousKey.current = key;
    // Briefly mark the section the guide pointed to, so the visitor can find it.
    if (!fromAssistant || !hash || !target) return;
    target.classList.add('assistant-target');
    const timer = window.setTimeout(
      () => target.classList.remove('assistant-target'),
      2400,
    );
    return () => {
      window.clearTimeout(timer);
      target.classList.remove('assistant-target');
    };
  }, [hash, pathname, key, state]);
  // Sections below the fold ease in once, the first time they are scrolled to.
  useEffect(() => {
    const items = Array.from(
      document.querySelectorAll<HTMLElement>('[data-reveal]'),
    );
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }),
      { rootMargin: '0px 0px -8% 0px' },
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [pathname]);
  return null;
}

interface EntryProps {
  title: string;
  subtitle?: string;
  location: string;
  dates: string;
  children?: React.ReactNode;
}

function Entry({ title, subtitle, location, dates, children }: EntryProps) {
  return (
    <article className="entry">
      <header className="entry-head">
        <div>
          <h3>{title}</h3>
          {subtitle && <p className="entry-subtitle">{subtitle}</p>}
        </div>
        <p className="entry-meta">
          <span>{dates}</span>
          <span>{location}</span>
        </p>
      </header>
      {children}
    </article>
  );
}

interface SectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

function Section({ id, title, children }: SectionProps) {
  return (
    <section
      id={id}
      tabIndex={-1}
      className="section"
      aria-labelledby={`${id}-heading`}
      data-reveal
    >
      <h2 className="section-title" id={`${id}-heading`}>
        {title}
      </h2>
      <div className="section-body">{children}</div>
    </section>
  );
}

function AboutPage() {
  return (
    <article className="page">
      <header className="hero stagger" id="about-content" tabIndex={-1}>
        <p className="eyebrow">
          <span>Software engineer</span>
          <span>Chicago, Illinois</span>
        </p>
        <h1 tabIndex={-1}>Stephen Bowen</h1>
        <p className="lead">
          I build cloud applications, agentic AI solutions, and web
          applications. My work spans Microsoft Power Platform, Azure, and GCP.
        </p>
        <div className="hero-actions">
          <Link className="button" to="/projects">
            View projects <Icon name="arrow-right" />
          </Link>
          <a className="text-link" href={contactLinks.email}>
            Email me <Icon name="arrow-up-right" />
          </a>
        </div>
      </header>

      <Section id="experience" title="Experience">
        <Entry
          title="Freelance Software Development"
          location="Remote"
          dates="Since 2026"
        >
          <ul className="dash-list">
            <li>
              Develop cloud-based GCP solutions for AI agent orchestration and
              web development.
            </li>
          </ul>
        </Entry>

        <Entry
          title="Root16 Reply"
          subtitle="Senior Software Engineer"
          location="Chicago, IL"
          dates="Since 2025"
        >
          <ul className="dash-list">
            <li>
              Architect, deploy, and support bespoke Microsoft Power Platform
              solutions that automate manual business processes, reducing
              operational overhead for enterprise consulting clients.
            </li>
            <li>
              Serve as the organization's go-to expert on frontier AI, guiding
              project teams on LLM adoption and intelligent agent design to
              maximize the firm’s competitive advantage in delivering
              next-generation client solutions.
            </li>
            <li>
              Mentor junior and mid-level developers, raising team code quality
              and enabling them to deliver features independently.
            </li>
            <li>
              Developed a custom Copilot Studio agent that performs RAG over a
              REST API-backed knowledge base, providing role specific
              self-service support and optimizing valuable help desk resources.
            </li>
            <li>
              Leading two internal projects building harness-agnostic agent
              tooling (MCP servers, skills, automations) that increase QA
              productivity and expand test coverage for all clients.
            </li>
          </ul>
        </Entry>

        <Entry
          title="ArchitectNow"
          subtitle="Software Development Engineer"
          location="Remote"
          dates="2025"
        >
          <ul className="dash-list">
            <li>
              Delivered consulting, development, and ongoing support for 10+
              clients across varying stacks and business verticals, sustaining
              long-term client engagements.
            </li>
            <li>
              Planned, estimated, and led the parallelization of a Python
              application that processes and classifies LiDAR data across 150+
              nodes in North America, significantly increasing the client's data
              processing throughput.
            </li>
            <li>
              Modernized a business-critical Windows application and dozens of
              dependent processes into a cloud-based web application using the
              Strangler Fig pattern to deliver modernized features incrementally
              with minimal disruption to users.
            </li>
            <li>
              Led feature development for two new financial assistance programs
              for a large charity, enabling the client to accept applications,
              securely collect PII and sensitive documents, aggregate and report
              financial data, and manage fund allocation.
            </li>
            <li>
              Optimized a touch-enabled application from Angular 8 to Angular
              17, replacing outdated libraries and resolving incompatibilities
              and styling bugs, making UI paints and actions 3x faster for end
              users.
            </li>
          </ul>
          <ul className="role-list">
            <li>
              <span>Junior Software Development Engineer</span>
              <span>2022-2025</span>
            </li>
            <li>
              <span>Software Development Engineer Intern</span>
              <span>2022</span>
            </li>
          </ul>
        </Entry>
      </Section>

      <Section id="education" title="Education">
        <Entry
          title="University of Missouri College of Engineering"
          subtitle="B.S. in Computer Science with honors"
          location="Columbia, MO"
          dates="2019-2023"
        />
      </Section>

      <Section id="certifications" title="Certifications">
        <ul className="link-list">
          {certifications.map(({ label, href }) => (
            <li key={href}>
              <a
                className="link-row"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>{label}</span>
                <Icon name="arrow-up-right" />
              </a>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="skills" title="Skills">
        <div className="skill-group">
          <h3>Programming languages</h3>
          <ul className="inline-list">
            {languages.map((language) => (
              <li key={language}>{language}</li>
            ))}
          </ul>
        </div>
        <div className="skill-group">
          <h3>Skills and technologies</h3>
          <ul className="inline-list">
            {technologies.map((technology) => (
              <li key={technology}>{technology}</li>
            ))}
          </ul>
        </div>
      </Section>
    </article>
  );
}

function ProjectsPage() {
  return (
    <article className="page">
      <header className="page-head stagger">
        <p className="eyebrow">
          <span>Selected work</span>
        </p>
        <h1 tabIndex={-1}>Projects</h1>
        <p className="lead">A few projects I’ve worked on.</p>
      </header>
      <div className="project-list">
        {projects.map((project, index) => (
          <article
            className="project"
            id={project.id}
            tabIndex={-1}
            key={project.id}
            data-reveal
          >
            <p className="project-index">
              <span>0{index + 1}</span>
              <span>{project.kind}</span>
            </p>
            <div className="project-body">
              <header className="entry-head">
                <h2>{project.name}</h2>
                <p className="entry-meta">
                  <span>{project.dates}</span>
                </p>
              </header>
              <p className="project-description">{project.description}</p>
              <ul className="dash-list">
                {project.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
              <a
                className="text-link"
                href={project.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {project.linkLabel} <Icon name="arrow-up-right" />
              </a>
            </div>
          </article>
        ))}
      </div>
    </article>
  );
}

function ContactPage() {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  );
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText('sfbowen4.5@gmail.com');
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };
  return (
    <article className="page">
      <header className="page-head stagger">
        <p className="eyebrow">
          <span>Contact</span>
        </p>
        <h1 tabIndex={-1}>Get in touch</h1>
        <p className="lead">
          You can reach me by email or find me on the profiles below.
        </p>
      </header>
      <div className="contact-email" data-reveal>
        <a className="contact-address" href={contactLinks.email}>
          sfbowen4.5@gmail.com
        </a>
        <div className="contact-actions">
          <button
            className="button button-quiet"
            type="button"
            onClick={copyEmail}
          >
            Copy email
          </button>
          <span className="copy-status" role="status">
            {copyState === 'copied'
              ? 'Copied to clipboard.'
              : copyState === 'failed'
                ? 'Select the email above to copy it.'
                : ''}
          </span>
        </div>
      </div>
      <ul
        className="link-list contact-list"
        aria-label="Contact Stephen Bowen"
        data-reveal
      >
        {profiles.map(({ label, href, handle }) => (
          <li key={label}>
            <a
              className="link-row"
              href={href}
              {...(href.startsWith('mailto:')
                ? {}
                : { target: '_blank', rel: 'noopener noreferrer' })}
            >
              <span>{label}</span>
              {/* The handle repeats the destination, so the link is named by its label alone. */}
              <span className="link-row-detail" aria-hidden="true">
                {handle}
              </span>
              <Icon name="arrow-up-right" />
            </a>
          </li>
        ))}
      </ul>
    </article>
  );
}

function NotFoundPage() {
  return (
    <article className="page">
      <header className="page-head stagger">
        <h1 tabIndex={-1}>Page not found</h1>
        <p className="lead">This page isn’t part of the portfolio.</p>
        <div className="hero-actions">
          <Link className="text-link" to="/">
            Back to the portfolio
          </Link>
        </div>
      </header>
    </article>
  );
}

class AssistantBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed)
      return (
        <button
          className="assistant-launcher"
          type="button"
          onClick={() => window.location.reload()}
        >
          Chat couldn’t load. Reload to retry.
        </button>
      );
    return this.props.children;
  }
}

function AppContent() {
  const [assistantIsOpen, setAssistantIsOpen] = useState(false);
  const [assistantLoaded, setAssistantLoaded] = useState(false);
  const openAssistant = () => {
    setAssistantLoaded(true);
    setAssistantIsOpen(true);
  };
  const launcher = (
    <button
      className="assistant-launcher"
      type="button"
      aria-expanded={assistantIsOpen}
      onClick={openAssistant}
    >
      talk to my mac mini <Icon name="arrow-up-right" />
    </button>
  );

  return (
    <div className={`app-shell${assistantIsOpen ? ' assistant-is-open' : ''}`}>
      <RouteEffects />
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <TopNav />
      <main id="main-content">
        <Routes>
          <Route path="/" element={<AboutPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <footer className="site-footer">
        <div className="footer-inner">
          <p>
            <Link to="/">Stephen Bowen</Link>
            <span>Chicago, Illinois</span>
          </p>
          <nav className="footer-links" aria-label="Profiles">
            {profiles.map(({ label, href }) => (
              <a
                key={label}
                href={href}
                {...(href.startsWith('mailto:')
                  ? {}
                  : { target: '_blank', rel: 'noopener noreferrer' })}
              >
                {label}
              </a>
            ))}
          </nav>
        </div>
      </footer>
      {assistantLoaded ? (
        <AssistantBoundary>
          <Suspense fallback={launcher}>
            <FloatingAssistant
              isOpen={assistantIsOpen}
              onOpenChange={setAssistantIsOpen}
            />
          </Suspense>
        </AssistantBoundary>
      ) : (
        launcher
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
